/**
 * Mixin with TableMovement invites functionality
 */
import { ANNOUNCEMENT_TYPES, INVITE_STATUSES, INVITE_TYPES, WSS_MESSAGE_TYPES } from "@/helpers/constants";
import { mapGetters } from "vuex";

export default {
  computed: {
    /**
     * Sent table movement invites
     * @return {Array}
     */
    ...mapGetters({
      groupTerminology: "room/groupTerminology"
    }),
    sentTableMovementInvites() {
      return this.$store.getters["invites/sentTableMovementInvites"];
    },
    /**
     * Announcements config
     * @return {Object}
     */
    announcementsConfig() {
      return this.$store.state.event.config.defaultAnnouncementsConfig;
    }
  },
  methods: {
    /**
     * Show Table Movement invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async showTableMovementInvite(invite) {
      let header = this.systemHeaders.TABLE_MOVEMENT_INVITE.format(this.groupTerminology);
      let body = this.systemMessages.TABLE_MOVEMENT_INVITE.format(
        invite.messageFrom,
        this.groupTerminology.toLowerCase(),
        invite.messageBody.tableName,
        this.groupTerminology.toLowerCase()
      );
      let message = `${header} ${body}`;
      let announcementConfig = this.announcementsConfig[ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_SEND];

      let notification = this.$notify(
        Object.assign(
          {
            type: "warning",
            HTMLMessage: message,
            buttons: [
              {
                name: "Accept",
                type: "success",
                handler: async () => {
                  notification.close();
                  await this.acceptTableMovementInvite(invite);
                }
              },
              {
                name: "Decline",
                type: "danger",
                handler: async () => {
                  notification.close();
                  await this.declineTableMovementInvite(invite);
                }
              }
            ],
            multiLine: true,
            hideIcon: true,
            top: true,
            right: true,
            showClose: false,
            closeDelay: null
          },
          announcementConfig || {}
        )
      );

      this.$store.commit("invites/addDisplayingInvite", { invite, notification });
    },

    /**
     * Accept Table Movement invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async acceptTableMovementInvite(invite) {
      let oldTableUUID = this.$store.getters["tables/userTable"].uuid;

      // Decline all other invites
      this.receivedInvites.forEach(noRelevantInvite => {
        if (noRelevantInvite.inviteType === INVITE_TYPES.TABLE_MOVEMENT && noRelevantInvite.uuid !== invite.uuid) {
          this.declineTableMovementInvite(noRelevantInvite);
        }
      });

      let { success } = await this.$store.dispatch("tables/changeUserTable", {
        destinationTableUUID: invite.messageBody.tableUUID
      });

      if (success) {
        await this.$store.dispatch("invites/updateInvite", {
          invite: invite,
          data: {
            inviteStatus: INVITE_STATUSES.ACCEPT
          }
        });
        await this.$store.dispatch("socket/sendInfoMessage", {
          messageBody: this.systemMessages.USER_MOVEMENT_INVITE_LEFT_TABLE.format(
            this.$store.getters["users/userFullName"],
            this.groupTerminology.toLowerCase()
          ),
          messageType: WSS_MESSAGE_TYPES.TABLE_MESSAGE,
          groupUUID: oldTableUUID,
          isHTML: true
        });
        await this.$store.dispatch("socket/sendInfoMessage", {
          messageBody: this.systemMessages.USER_MOVEMENT_INVITE_JOINED_TABLE.format(
            this.$store.getters["users/userFullName"],
            this.groupTerminology.toLowerCase(),
            invite.messageBody.inviterFullName
          ),
          messageType: WSS_MESSAGE_TYPES.TABLE_MESSAGE,
          groupUUID: invite.messageBody.tableContentNodeUUID,
          isHTML: true
        });
        await this.$store.dispatch("chat/getTableMessages");
      }

      this.hideInvite(invite);
    },

    /**
     * Decline Table Movement invite
     * @param invite {Object}
     * @return {Promise<void>}
     */
    async declineTableMovementInvite(invite) {
      await this.$store.dispatch("invites/updateInvite", {
        invite: invite,
        data: {
          inviteStatus: INVITE_STATUSES.DECLINE_BY_INVITEE
        }
      });

      this.hideInvite(invite);
    }
  }
};
